import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdventurePayload } from './types'
import { Table, TableBody, TableCell, TableRow } from '../components/table'
import { Button } from '../components/button'
import { PlusIcon } from '@heroicons/react/20/solid'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../components/dialog'
import { useAuthStatus } from './user_dropdown'

function AddAdventureModal({ open, onClose, onCreated }: {
  open: boolean
  onClose: () => void
  onCreated: (id: number) => void
}) {
  const [title, setTitle] = useState('')
  const [saving, setSaving] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setTitle('')
      setTimeout(() => titleRef.current?.focus(), 100)
    }
  }, [open])

  const handleSubmit = async () => {
    if (!title.trim()) return
    setSaving(true)
    try {
      const res = await fetch('/v0/adventures', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim() }),
      })
      if (!res.ok) return
      const { id } = await res.json()
      onCreated(id)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create Adventure</DialogTitle>
      <DialogDescription>Give your adventure a title.</DialogDescription>
      <DialogBody>
        <input
          ref={titleRef}
          type="text"
          placeholder="Adventure title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
          className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
        />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="airtime" onClick={handleSubmit} disabled={!title.trim() || saving}>
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function AdventureList() {
  const navigate = useNavigate()
  const { userData } = useAuthStatus()
  const [adventures, setAdventures] = useState<AdventurePayload[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)

  useEffect(() => {
    fetch('/v0/adventures')
      .then(r => r.json())
      .then(data => setAdventures(data.adventures ?? []))
      .finally(() => setIsLoading(false))
  }, [])

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-[#2D3142] dark:text-white">Adventures</h1>
          <p className="mt-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
            Multi-flight groupings and trip reports.
          </p>
        </div>
        {userData.authenticated && (
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <Button color="airtime" onClick={() => setAddOpen(true)}>
              <PlusIcon data-slot="icon" />
              New Adventure
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="mt-6 animate-pulse space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 bg-[#BFC0C0]/30 rounded" />
          ))}
        </div>
      ) : adventures.length === 0 ? (
        <p className="mt-8 text-center text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
          No adventures yet.
        </p>
      ) : (
        <div className="mt-6 bg-white dark:bg-[#2D3142] shadow-lg rounded-lg overflow-hidden">
          <Table>
            <TableBody>
              {adventures.map(a => (
                <TableRow
                  key={a.id}
                  className="hover:bg-[#BFC0C0]/30 dark:hover:bg-[#4F5D75]/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/adventure/${a.id}`)}
                >
                  <TableCell className="font-medium text-[#2D3142] dark:text-white">
                    {a.title}
                  </TableCell>
                  <TableCell className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
                    {formatDate(a.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddAdventureModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(id) => navigate(`/adventure/${id}`)}
      />
    </div>
  )
}
